import React from 'react';
import { X } from 'lucide-react';

export type BadgeVariant = 
  | 'blue' 
  | 'green' 
  | 'red' 
  | 'yellow' 
  | 'purple' 
  | 'gray' 
  | 'teal' 
  | 'orange'
  | 'indigo';

export type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  removable?: boolean;
  onRemove?: () => void;
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  green: 'bg-green-100 text-green-800 border-green-200',
  red: 'bg-red-100 text-red-800 border-red-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200',
  gray: 'bg-gray-100 text-gray-800 border-gray-200',
  teal: 'bg-teal-100 text-teal-800 border-teal-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200',
  indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
};

const sizeClasses: Record<BadgeSize, { badge: string; text: string; removeButton: string }> = {
  sm: {
    badge: 'px-2 py-0.5',
    text: 'text-xs',
    removeButton: 'p-0.5',
  },
  md: {
    badge: 'px-2 py-1',
    text: 'text-xs',
    removeButton: 'p-0.5',
  },
  lg: {
    badge: 'px-3 py-1.5',
    text: 'text-sm',
    removeButton: 'p-1',
  },
};

const hoverClasses: Record<BadgeVariant, string> = {
  blue: 'hover:bg-blue-200',
  green: 'hover:bg-green-200',
  red: 'hover:bg-red-200',
  yellow: 'hover:bg-yellow-200',
  purple: 'hover:bg-purple-200',
  gray: 'hover:bg-gray-200',
  teal: 'hover:bg-teal-200',
  orange: 'hover:bg-orange-200',
  indigo: 'hover:bg-indigo-200',
};

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'gray',
  size = 'md',
  removable = false,
  onRemove,
  className = '',
}) => {
  const variantClass = variantClasses[variant];
  const sizeClass = sizeClasses[size];
  const hoverClass = hoverClasses[variant];

  return (
    <span
      className={`
        inline-flex items-center gap-1 font-medium rounded-full border
        ${variantClass}
        ${sizeClass.badge}
        ${sizeClass.text}
        ${className}
      `}
    >
      {children}
      {removable && onRemove && (
        <button
          onClick={onRemove}
          className={`
            ml-1 rounded-full transition-colors
            ${hoverClass}
            ${sizeClass.removeButton}
          `}
          title="Remover"
          type="button"
        >
          <X size={size === 'lg' ? 14 : 12} />
        </button>
      )}
    </span>
  );
}; 